/**
 * PortalGuard — mantém cada usuário na área "casa" do seu ambiente (E50.F2).
 *
 * Duas posturas deliberadas (ADR-0059):
 *   - `area="portal"` é fail-closed: enquanto o perfil não resolve, nada do
 *     portal renderiza; ambiente `interno` (ou erro de perfil) volta para a
 *     área do perito. O portal é novo — não há legado a preservar.
 *   - `area="perito"` é fail-open: o comportamento pré-E50 é intocado
 *     (loading/erro/`interno` renderizam normalmente); só redireciona quando o
 *     perfil AFIRMA que o usuário mora no portal. Payloads de /api/perfil sem
 *     `ambiente` valem `interno` (default em getPerfil), então nenhuma rota do
 *     perito muda de comportamento até a E50.F3 ligar o campo no backend.
 */
import { type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import type { Ambiente } from '@/lib/auth';
import {
  homeRouteForAmbiente,
  PERITO_HOME_ROUTE,
  TROCA_DE_SENHA_ROUTE,
} from '@/lib/auth/homeRoute';

export type PortalGuardArea = 'portal' | 'perito';

/** Régua de ÁREA: para onde mandar, ou `null` para renderizar o que foi pedido. */
function destinoDaArea(
  area: PortalGuardArea,
  estado: { ambiente: Ambiente | null; loading: boolean; error: boolean },
): string | null {
  const { ambiente, loading, error } = estado;
  const moraNoPortal = ambiente !== null && ambiente !== 'interno';
  if (area === 'perito') {
    return !loading && !error && moraNoPortal ? homeRouteForAmbiente(ambiente) : null;
  }
  if (loading) return null;
  return moraNoPortal && !error ? null : PERITO_HOME_ROUTE;
}

export function PortalGuard({
  area,
  children,
}: {
  area: PortalGuardArea;
  children: ReactElement;
}): ReactElement {
  const estado = useCurrentRole();
  const perfilResolvido = !estado.loading && !estado.error;

  // E65.F3 (ADR-0069): antes de decidir QUAL área, decide se a pessoa pode
  // alcançar alguma. É a única checagem daqui que não olha `ambiente` — o
  // servidor recusa igual nos dois.
  //
  // Não redireciona em `loading`/`error` de propósito: quem barra é o servidor,
  // com 403. A tela só evita que a pessoa colecione erros sem explicação, e um
  // redirect especulativo sobre perfil que ainda não resolveu prenderia quem não
  // tem pendência nenhuma.
  if (perfilResolvido && estado.senhaProvisoria) {
    return <Navigate to={TROCA_DE_SENHA_ROUTE} replace />;
  }

  const destino = destinoDaArea(area, estado);
  if (destino !== null) {
    return <Navigate to={destino} replace />;
  }
  // O portal é fail-closed: enquanto o perfil não resolve, nada dele renderiza.
  if (area === 'portal' && estado.loading) {
    return <div data-testid="portal-guard-loading" />;
  }
  return children;
}
